import { useTranslation } from 'react-i18next';
import { DragIndicator, Phone } from '@mui/icons-material';
import {
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  DialogActions,
  Stack,
  Avatar,
  Divider,
  Fab,
} from '@mui/material';
import { Call } from '@twilio/voice-sdk';

import { client } from '@frontend/features/common/services';
import { useTwilio } from '@frontend/features/communications/TwilioContextProvider/provider';
import { ClientResponseDto } from '@bandido/shared/dtos';

interface IncomingCallModalProps {
  open: boolean;
  connection?: Call;
  onAccept: () => void;
}

const IncomingCallModal = ({ open, onAccept }: IncomingCallModalProps) => {
  const { t } = useTranslation();
  const { callData } = useTwilio();

  const getClientName = (calleeInfo?: ClientResponseDto) =>
    calleeInfo?.firstName || calleeInfo?.lastName
      ? `${calleeInfo.firstName ?? ''} ${calleeInfo.lastName ?? ''}`
      : t('calls.incoming.unknown');

  const handleAnswer = async () => {
    try {
      await client.post('calls/take', {
        callSid: callData?.callSid,
        joiningCall: callData?.isJoining,
      });

      onAccept();
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Dialog
      PaperProps={{
        sx: {
          backgroundColor: (theme) => theme.palette.grey[900],
          width: 367,
          height: 400,
        },
      }}
      open={open}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle
        color="primary.contrastText"
        component="div"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: 2 }}
      >
        <DragIndicator />
        <Typography color="primary.contrastText" variant="subtitle1">
          {t('calls.incoming.title')}
        </Typography>
      </DialogTitle>
      <Divider
        sx={{
          backgroundColor: (theme) => theme.palette.grey[600],
        }}
      />
      <DialogContent sx={{ overflow: 'hidden' }}>
        <Stack gap={2} justifyContent="center">
          <Avatar sx={{ width: 60, height: 60, margin: '0 auto' }} />
          <Stack>
            <Typography color="primary.contrastText" variant="h6" align="center">
              {getClientName(callData?.calleeInfo)}
            </Typography>
            <Typography color="grey.400" variant="body2" align="center">
              {t('calls.incoming.contentSubtitle')}
            </Typography>
          </Stack>
          <Stack
            sx={{
              marginTop: 2,
            }}
            direction="row"
            justifyContent="center"
          >
            <Fab color="success" aria-label="add" onClick={handleAnswer}>
              <Phone />
            </Fab>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: (theme) => theme.spacing(3, 2, 3),
          gap: 4,
        }}
      >
        <Divider
          sx={{
            width: '100%',
            borderColor: (theme) => theme.palette.grey[600],
          }}
        />
        <Stack
          sx={{
            width: '100%',
            display: 'flex',
            margin: '0 !important',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}
        >
          <Typography color="grey.400" variant="body2" align="center">
            {t('calls.incoming.phone')}
          </Typography>
          <Typography color="primary.contrastText" variant="body2" align="center">
            {callData?.from}
          </Typography>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default IncomingCallModal;
